// MobileConfigureSource — fluxo REAL de "configurar fonte" mobile. Parede A6.
//
// A conta Swipe já está logada (sessão Swipe); falta a FONTE de conteúdo. Esta
// tela cadastra/valida a fonte pelo MESMO endpoint do site (apps/site/app/
// actions/preference.ts): PUT /v1/swipe-account/preference com CÓDIGO do
// provedor + usuário/senha do painel Xtream (+ URL do servidor opcional).
//
// REUSO: api.setSwipePreference (PUT preference, Bearer Swipe) + errorToMessage
// (mapeia provider_not_found / invalid_xtream_credentials / provider_inactive /
// xtream_account_expired / endpoint_belongs_to_provider) + mobile-source.ts
// (validação pura). Ao VALIDAR (validatedAt!=null) o trial pleno libera na hora
// (regra I0, já feita no backend); invalidamos as queries pra o app SAIR do
// estado no_provider e mostrar o catálogo.
//
// Touch puro: inputs com teclado nativo, alvos >= 48px, sem foco de remoto.

import { useTranslation } from '@app-iptv/i18n'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api } from '../api'
import { errorToMessage } from '../error-messages'

import {
  outcomeFromResponse,
  validateSourceForm,
  type SourceFormErrors,
  type SourceSaveOutcome,
} from './mobile-source'
import { bootstrapMobileSync } from './mobile-sync-bootstrap'
import styles from './MobileAuth.module.css'

export interface MobileConfigureSourceProps {
  /** Fonte cadastrada+validada (ou salva best-effort) — o shell fecha o overlay
   *  e volta pro catálogo (que agora carrega, sem no_provider). */
  onConfigured: () => void
}

export function MobileConfigureSource({ onConfigured }: MobileConfigureSourceProps): JSX.Element {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [providerCode, setProviderCode] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [serverUrl, setServerUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<SourceFormErrors>({})
  const [topError, setTopError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<SourceSaveOutcome | null>(null)

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (busy) return
    setTopError(null)
    setOutcome(null)

    const result = validateSourceForm({
      providerCode,
      xtreamUsername: username,
      xtreamPassword: password,
      serverUrl,
    })
    if (!result.ok) {
      setErrors(result.errors)
      if (result.errors.form) setTopError(t(result.errors.form))
      return
    }
    setErrors({})

    setBusy(true)
    try {
      const res = await api.setSwipePreference(result.params)
      // Fonte gravada na conta: o contexto de sync deste aparelho passa a ser ela
      // (perfis da conta + histórico). Fire-and-forget, nunca lança.
      void bootstrapMobileSync()
      // Fonte mudou: invalida TODAS as queries pra o catálogo (no_provider) e o
      // estado de trial refletirem a fonte nova sem stale. Idempotente.
      await queryClient.invalidateQueries()
      const out = outcomeFromResponse(res)
      setOutcome(out)
      // Validada => libera pleno e cai no catálogo na hora. Salva-sem-verificar
      // => também sai do no_provider (a fonte existe), mas mostra o aviso antes.
      if (out.status === 'validated') {
        onConfigured()
      }
    } catch (err) {
      setTopError(errorToMessage(err, t))
    } finally {
      setBusy(false)
    }
  }

  // Estado "salvo mas não verificado agora" (Xtream fora do ar): mostra aviso +
  // botão pra continuar mesmo assim (a fonte já está ativa).
  if (outcome?.status === 'saved-unverified') {
    return (
      <div className={styles.screen} data-screen="configure-source" data-state="saved-unverified">
        <div className={styles.content}>
          <header className={styles.header}>
            <p className={styles.brand}>{t('mobileSource.title')}</p>
            <h1 className={styles.title}>{t('mobileSource.savedTitle')}</h1>
            <p className={styles.subtitle}>{t('mobileSource.savedUnverified')}</p>
          </header>
          <button
            type="button"
            className={`${styles.button} ${styles.primary}`}
            onClick={onConfigured}
          >
            {t('mobileSource.continue')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.screen} data-screen="configure-source">
      <div className={styles.content}>
        <header className={styles.header}>
          <p className={styles.brand}>{t('mobileSource.title')}</p>
          <h1 className={styles.title}>{t('mobileHome.noSourceTitle')}</h1>
          <p className={styles.subtitle}>{t('mobileSource.subtitle')}</p>
        </header>

        <form className={styles.form} onSubmit={(e) => void handleSubmit(e)} noValidate>
          {topError && (
            <p className={styles.error} role="alert">
              {topError}
            </p>
          )}

          <div className={styles.field}>
            <label className={styles.label} htmlFor="mobile-source-code">
              {t('mobileSource.codeLabel')}
            </label>
            <input
              id="mobile-source-code"
              className={`${styles.input} ${errors.providerCode ? styles.inputError : ''}`}
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={8}
              placeholder={t('mobileSource.codePlaceholder')}
              value={providerCode}
              onChange={(e) => setProviderCode(e.target.value)}
              disabled={busy}
            />
            {errors.providerCode && (
              <span className={styles.fieldError}>{t(errors.providerCode)}</span>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="mobile-source-user">
              {t('mobileSource.userLabel')}
            </label>
            <input
              id="mobile-source-user"
              className={`${styles.input} ${errors.xtreamUsername ? styles.inputError : ''}`}
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={120}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={busy}
            />
            {errors.xtreamUsername && (
              <span className={styles.fieldError}>{t(errors.xtreamUsername)}</span>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="mobile-source-pass">
              {t('mobileSource.passwordLabel')}
            </label>
            <input
              id="mobile-source-pass"
              className={`${styles.input} ${errors.xtreamPassword ? styles.inputError : ''}`}
              type="password"
              autoComplete="off"
              maxLength={120}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
            {errors.xtreamPassword && (
              <span className={styles.fieldError}>{t(errors.xtreamPassword)}</span>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="mobile-source-url">
              {t('mobileSource.urlLabel')}
            </label>
            <input
              id="mobile-source-url"
              className={`${styles.input} ${errors.serverUrl ? styles.inputError : ''}`}
              type="url"
              inputMode="url"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={255}
              placeholder="http://servidor:porta"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              disabled={busy}
            />
            {errors.serverUrl ? (
              <span className={styles.fieldError}>{t(errors.serverUrl)}</span>
            ) : (
              <span className={styles.hint}>{t('mobileSource.urlHint')}</span>
            )}
          </div>

          <button type="submit" className={`${styles.button} ${styles.primary}`} disabled={busy}>
            {busy ? (
              <span className={styles.spinner} aria-hidden="true" />
            ) : (
              t('mobileSource.submit')
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
